import { type LatLng, latLng } from "leaflet";

import Converter from "./converter";

interface CeilingDetails {
    tileSizeX: number;
    tileSizeY: number;
    gridOffSetX: number;
    gridOffSetY: number;
}

type LatLngLike = Pick<LatLng, "lat" | "lng">;

/**
 * Finds the ceiling grid intersection closest to a point.
 *
 * @param point - The point to resolve.
 * @param tileSizeX - The horizontal tile size, in feet.
 * @param tileSizeY - The vertical tile size, in feet.
 * @param gridOffSetX - The horizontal grid offset, in feet.
 * @param gridOffSetY - The vertical grid offset, in feet.
 * @returns The nearest grid intersection.
 */
export const getNearestGridPoint = (
    point: LatLngLike,
    tileSizeX: number,
    tileSizeY: number,
    gridOffSetX: number,
    gridOffSetY: number,
): LatLng => {
    const xGrid = Converter.convertFeetToMeters(tileSizeX);
    const yGrid = Converter.convertFeetToMeters(tileSizeY);
    const gridOffsetX = Converter.convertFeetToMeters(gridOffSetX % tileSizeX);
    const gridOffsetY = Converter.convertFeetToMeters(gridOffSetY % tileSizeY);

    const lngLineNoOffset = Math.round(point.lng / xGrid) * xGrid;
    const latLineNoOffset = Math.round(point.lat / yGrid) * yGrid;

    const lngPlusOffset = lngLineNoOffset + gridOffsetX;
    const lngMinusOffset = lngLineNoOffset + gridOffsetX - xGrid;

    const latPlusOffset = latLineNoOffset + gridOffsetY;
    const latMinusOffset = latLineNoOffset + gridOffsetY - yGrid;

    const nearestLngLine =
        Math.abs(point.lng - lngPlusOffset) <
        Math.abs(point.lng - lngMinusOffset)
            ? lngPlusOffset
            : lngMinusOffset;
    const nearestLatLine =
        Math.abs(point.lat - latPlusOffset) <
        Math.abs(point.lat - latMinusOffset)
            ? latPlusOffset
            : latMinusOffset;

    return latLng(nearestLatLine, nearestLngLine);
};

/**
 * Snaps a point to the nearest intersection of the ceiling tile grid.
 *
 * @param point - The point to snap.
 * @param ceilingDetails - The tile sizes and grid offsets to snap against.
 * @returns The snapped point.
 */
export const snapToGrid = (
    point: LatLng,
    { tileSizeX, tileSizeY, gridOffSetX, gridOffSetY }: CeilingDetails,
): LatLng =>
    getNearestGridPoint(point, tileSizeX, tileSizeY, gridOffSetX, gridOffSetY);
